import OneSignal from 'react-onesignal';
import { IS_PRERENDERING, MODE, ONESIGNAL_APP_ID } from '@/base/constants';

type PushStatus =
  | 'ok'
  | 'not-supported'
  | 'no-permission-granted'
  | 'not-opted-in';

let initPromise: null | Promise<void> = null;
const appId = ONESIGNAL_APP_ID;

const init = async () => {
  if (!appId || IS_PRERENDERING) return false;
  if (initPromise === null) {
    initPromise = (async () => {
      if (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        MODE === 'production'
      ) {
        try {
          await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('Service worker ready timeout')),
                5000,
              ),
            ),
          ]);
        } catch (e) {
          console.error(
            'OneSignal: service worker not ready in time, proceeding with init.',
            e,
          );
        }
      }
      await OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: MODE === 'development',
        autoResubscribe: true,
        autoRegister: false,
        autoPrompt: false,
        serviceWorkerPath: '/sw.js',
        serviceWorkerParam: { scope: '/' },
      });
    })();
    await initPromise;
  }
  return await initPromise;
};

const setExternalId = async (
  externalId?: string,
  email?: string,
): Promise<void> => {
  const initiaed = await init();
  if (initiaed === false) return;
  if (externalId) {
    await OneSignal.login(externalId);
    if (email) {
      await OneSignal.User.addAlias('email', email);
    }
  } else {
    await OneSignal.logout();
  }
};

const getPushStatus = (): PushStatus => {
  let returnValue: PushStatus = 'ok';
  if (!OneSignal.Notifications.isPushSupported()) {
    returnValue = 'not-supported';
  } else if (!OneSignal.Notifications.permission) {
    returnValue = 'no-permission-granted';
  } else if (!OneSignal.User.PushSubscription.optedIn) {
    returnValue = 'not-opted-in';
  }
  return returnValue;
};

const requestPush = async (): Promise<PushStatus> => {
  try {
    await OneSignal.setConsentGiven(true);
    await OneSignal.Notifications.requestPermission();
    await OneSignal.User.PushSubscription.optIn();
    return getPushStatus();
  } catch (_error) {
    return getPushStatus();
  }
};

const getSubscriptionID = async () => {
  const initiaed = await init();
  if (initiaed === false) return;
  return OneSignal.User.PushSubscription;
};

const enableNotifications = async () => {
  const initiaed = await init();
  if (initiaed === false) return;
  return await requestPush();
};

const disableNotifications = async () => {
  const initiaed = await init();
  if (initiaed === false) return;
  await OneSignal.setConsentGiven(false);
  await OneSignal.User.PushSubscription.optOut();
};

const setLanguage = async (language: string) => {
  const initiaed = await init();
  if (initiaed === false) return;
  await OneSignal.User.setLanguage(language);
};

export const oneSignal = {
  init,
  setExternalId,
  setLanguage,
  requestPush,
  getPushStatus,
  getSubscriptionID,
  enableNotifications,
  disableNotifications,
};
